import React, { ReactNode } from "react";
import AlgaIcon, { AlgaVariant } from "./AlgaIcon";
import { Skeleton, Typography, Grid, Container } from "@mui/material";
import { makeStyles } from "tss-react/mui";

type AlgaTooltipProps = {
  variant: AlgaVariant;
  text: string;
  children?: ReactNode;
  isLoading?: boolean;
};

const useStyles = makeStyles()((theme) => ({
  bubble: {
    position: "relative",
    padding: theme.spacing(1.5),
    borderRadius: "9px",
    background: theme.palette.warning.light,
    border: `2px solid ${theme.palette.warning.main}`,
    "&:after": {
      content: '""',
      position: "absolute",
      zIndex: 1,
      borderStyle: "solid",
      borderColor: `transparent ${theme.palette.warning.light}`,
      borderWidth: "15px 0 15px 15px",
      top: "50%",
      right: "-15px",
      marginTop: "-15px",
    },
    "&:before": {
      content: '""',
      position: "absolute",
      zIndex: 0,
      borderStyle: "solid",
      borderColor: `transparent ${theme.palette.warning.main}`,
      borderWidth: "17px 0 17px 17px",
      top: "50%",
      right: "-18px",
      marginTop: "-17px",
    },
  },
  "bubble-error": {
    borderColor: theme.palette.error.main,
    background: theme.palette.error.light,
    "&:after": {
      borderColor: `transparent ${theme.palette.error.light}`,
    },
    "&:before": {
      borderColor: `transparent ${theme.palette.error.main}`,
    },
  },
  "bubble-success": {
    borderColor: theme.palette.primary.main,
    background: theme.palette.success.light,
    "&:after": {
      borderColor: `transparent ${theme.palette.success.light}`,
    },
    "&:before": {
      borderColor: `transparent ${theme.palette.primary.main}`,
    },
  },
}));

export default function AlgaTooltip({
  variant,
  text,
  children,
  isLoading,
}: AlgaTooltipProps) {
  const { classes } = useStyles();

  const algaBubble = (
    <Grid
      textAlign="center"
      className={`
          ${classes.bubble}
          ${variant === "error" ? classes["bubble-error"] : ""}
          ${variant === "success" ? classes["bubble-success"] : ""}
        `}
    >
      <Typography variant="body1">{text}</Typography>
      {children && <Container>{children}</Container>}
    </Grid>
  );

  return (
    <Grid
      container
      alignItems="center"
      justifyContent="space-between"
      wrap="nowrap"
    >
      <Grid item xs={7}>
        {isLoading ? (
          <Skeleton variant="rounded">{algaBubble}</Skeleton>
        ) : (
          algaBubble
        )}
      </Grid>

      <Grid item xs={4} textAlign="center">
        {isLoading ? (
          <Skeleton variant="circular">
            <AlgaIcon variant={variant} size="medium" />
          </Skeleton>
        ) : (
          <AlgaIcon variant={variant} size="medium" />
        )}
      </Grid>
    </Grid>
  );
}
